/* eslint-disable */
import $ from '@nuskin/nuskinjquery';

/**
 * Exposed points commerce service. Applied within the cart checkout page. This
 * service is generic enough to be applied to either ADP or the loyalties program.
 * It's main focus is to manage point elements within the order summary (cart checkout) DOM.
 * The service interfaces with the cart object from local storage and tracks points
 * per product that are to be applied (redeemed). When a re-orderSimulate is invoked
 * these points will be summed up and applied to the redeemed point total for the order.
 *
 * PointsCheckoutService
 *
 * @namespace PointsCheckoutService
 */
let PointsCheckoutService = function () {

    // Globals:
    let dom = document;
    let SKU_REGEX = /^[0-9]{6,9}$/i;

    /**
     * Upon initialization the shopping cart is populated with the shop-cart object from local storage.
     * Once this is in memory, this service will update points dynamically per product (sku).
     */
    let shoppingCart;

    /**
     * Reset the point totals in the shopping cart and reset the point fields in the DOM.
     * @memberof PointsCheckoutService
     * @example
     * PointsCheckoutService.resetPoints(force)
     * @param force - bypass the check for if a points sku tuple checkbox is selected or not.
     * Basically this means to perform a hard reset of every point tuple and is optional.
     */
    function resetPoints(force) {
        if (!shoppingCart) return;

        shoppingCart.getItems({cartOrderItems: true}).forEach((obj) => {
            let sku = obj.getProduct().sku;
            try {
                if (sku && typeof sku === 'string' && SKU_REGEX.test(sku)) {
                    let purchaseWithPoints = dom.getElementById("purchaseWithPoints_" + sku);

                    if (purchaseWithPoints && (purchaseWithPoints.checked || force)) {
                        purchaseWithPoints.checked = false;

                        // See if there's a number increment field (i.e. for multiple qty's) and if there is
                        // no element, treat this as a single item for point redemption.
                        let nsNumberIncrement = dom.getElementById("nsNumberIncrement_" + sku);
                        if (!nsNumberIncrement) {
                            _setPointTotalPriceOnShoppingCart(sku, 0); // reset single item tuple
                            _updateOrderPointTotalBadges(_getPointTotalPriceOnShoppingCart());
                        } else {
                            togglePointFields(sku);
                        }
                    }
                } else {
                    throw new _skuFormatException(sku);
                }
            } catch (e) {
                console.warn(e.message);
            }
        });
    }

    /**
     * Toggle point fields and if the product quantity is greater then one, setup the event handler
     * for handling point increments. Point fields should only be displayed if the purchase with
     * points checkbox is selected. This function should be invoked one time within any controller that wishes
     * to utilize points in checkout.
     * @memberof PointsCheckoutService
     * @example
     * PointsCheckoutService.initPoints(cart)
     */
    function initPoints(cart) {
        shoppingCart = cart;
        shoppingCart.getItems({cartOrderItems: true}).forEach((obj) => {
            togglePointFields(obj.getProduct().sku, obj.getProduct().getPoints());
            // Only register number increment events for products that are more than one in quantity:
            if ((obj.qty > 1) && (obj.sapLineId === null)) {
                _registerSkuWithNumberIncrementEvent(obj.product.sku, obj.product.points);
            }
        });
        payForShippingUsingPoints();
        _updateOrderPointTotalBadges(0);
    }

    /**
     * resetPayForShippingUsingPoints
     * @memberof PointsCheckoutService
     * @example
     * PointsCheckoutService.resetPayForShippingUsingPoints()
     */
    function resetPayForShippingUsingPoints() {
        _setPayForShippingUsingPoints(false);
    }

    /**
     * Checks if the pay for shipping using points checkbox is selected, and if so updates
     * the order point total with shipping added.
     * @memberof PointsCheckoutService
     * @example
     * PointsCheckoutService.payForShippingUsingPoints()
     */
    function payForShippingUsingPoints() {
        if (!shoppingCart) {
            return;
        }
        let payForShippingUsingPoints = dom.getElementById("payForShippingUsingPoints");
        _setPayForShippingUsingPoints(payForShippingUsingPoints.checked);
    }

    /**
     *
     * @param selected
     * @private
     * @ignore
     */
    function _setPayForShippingUsingPoints(selected) {
        let payForShippingUsingPoints = dom.getElementById("payForShippingUsingPoints");
        let orderPointTotalBadge = ($(".orderPointTotalBadge")) ? $(".orderPointTotalBadge")[0] : dom.getElementById("orderPointTotalBadge");
        let payForShippingUsingPointsBadge = dom.getElementById("payForShippingUsingPointsBadge");
        let shippingCost = dom.getElementById("shippingCost");

        if (!payForShippingUsingPoints
            || !orderPointTotalBadge
            || !payForShippingUsingPointsBadge
            || !shippingCost) {
            console.warn('payForShippingUsingPoints consulted an invalid DOM structure.');
            return;
        }

        let shippingPoints = parseFloat(payForShippingUsingPointsBadge.innerHTML);
        if (selected) {
            // Apply points to shipping
            shippingCost.style.textDecoration = "line-through";
            orderPointTotalBadge.style.display = "initial";
            _setShippingPointsOnShoppingCart(shippingPoints);
            _updateOrderPointTotalBadges(_getPointTotalPriceOnShoppingCart());
        } else {
            // De-apply (remove) points from shipping
            shippingCost.style.textDecoration = "initial";
            _setShippingPointsOnShoppingCart(0);
            _updateOrderPointTotalBadges(_getPointTotalPriceOnShoppingCart());
        }
    }

    /**
     * Utility function utilized in functions that need to set the pointTotalPrice on a product.
     * The strategy is to set the point total price, then upon updating the point total price for the DOM
     * the getter is called to fetch the total points.
     *
     * @param sku determines what product needs the pointTotalPrice set.
     * @param qty quantity of this product to be purchased with points.
     * @param updatedPoints to be set to the pointTotalPrice.
     * @ignore
     */
    function _setPointTotalPriceOnShoppingCart(sku, qty, updatedPoints) {
        // Save the point total price to the product.
        // This will be utilized when the order point total is updated.
        if (!shoppingCart) return;

        shoppingCart.getItems({cartOrderItems: true}).filter(obj => ((obj.getProduct().sku === sku)))
            .forEach(obj => {
                obj.getProduct().pointTotalPrice = updatedPoints;
                obj.redeem = qty > 0;
                obj.qtyRedeemWithPoints = qty;
            });
    }

    /**
     * Set the shipping points on the cart. This indicates that the user has selected the option
     * to redeem points for shipping.
     *
     * @param shippingPoints
     * @ignore
     */
    function _setShippingPointsOnShoppingCart(shippingPoints) {
        if (!shoppingCart) return;

        shoppingCart.payForShippingWithPoints = shippingPoints;
    }

    /**
     * Utility function utilized in functions that need to get the point total by adding up all
     * the points per product (sku) in the shopping cart. This function factors in the shipping
     * cost.
     * @ignore
     */
    function _getPointTotalPriceOnShoppingCart() {
        if (!shoppingCart) return 0;

        let totalPoints = shoppingCart.getItems({cartOrderItems: true})
            .filter(obj => (typeof obj.getProduct().pointTotalPrice === 'number'))
            .map(obj => obj.product.pointTotalPrice)
            // Accumulator:
            .reduce((prev, curr) => (prev || 0) + (curr || 0), 0);
        return ((typeof shoppingCart.payForShippingWithPoints === 'number') && (shoppingCart.payForShippingWithPoints > 0))
            ? (shoppingCart.payForShippingWithPoints + totalPoints) : totalPoints;
    }

    /**
     * Invoke this function when you have an item with a checkout (purchase) quantity of one.
     * Such items have a slightly different markup so the DOM referencing needs to be handled
     * with special care.
     * @memberof PointsCheckoutService
     * @example
     * PointsCheckoutService.singleItemForPointPurchase(sku)
     * @param sku in string form
     */
    function singleItemForPointPurchase(sku) {
        try {
            if (sku && typeof sku === 'string' && SKU_REGEX.test(sku)) {
                let pointPriceBadge = dom.getElementById("pointPriceBadge_" + sku);
                if (dom.getElementById("purchaseWithPointsCheckbox_" + sku).checked) {
                    _setPointTotalPriceOnShoppingCart(sku, 1, parseFloat(pointPriceBadge.innerHTML));
                } else {
                    _setPointTotalPriceOnShoppingCart(sku, 0, 0);
                }
                _updateOrderPointTotalBadges(_getPointTotalPriceOnShoppingCart());
            }
            else {
                throw new _skuFormatException(sku);
            }
        } catch (e) {
            console.warn(e.message);
        }
    }


    /**
     * Toggle point fields per a specific sku tuple. Throw a custom exception if
     * no sku or an invalid sku is provided.
     * @memberof PointsCheckoutService
     * @example
     * PointsCheckoutService.togglePointFields(sku, points)
     * @param sku associated with fields to toggle. Must be of type string as some SKU's start with
     * a zero and handle that by encoding the digits within a string (required).
     *
     * @param points ratio associated with the sku (optional).
     */
    function togglePointFields(sku, points) {
        try {
            if (sku && typeof sku === 'string' && SKU_REGEX.test(sku)) {
                // if no points were passed in consult the shopping cart and see if the sku matches:
                if (!points && shoppingCart) {
                    shoppingCart.getItems({cartOrderItems: true}).forEach((obj) => {
                        if (obj.getProduct().sku === sku) {
                            if (obj.getProduct().getPoints()) {
                                points = obj.getProduct().getPoints();
                            }
                            // forEach doesn't allow break or it would be applied here (could make a custom BreakException)
                            // Not a big deal as there should only be a few objects in the cart to check
                        }
                    });
                }

                // Set or update point fields in the DOM:
                let orderPointTotalBadge = ($(".orderPointTotalBadge")) ? $(".orderPointTotalBadge")[0] : dom.getElementById("orderPointTotalBadge");
                let pointPrice = dom.getElementById("pointPrice_" + sku);
                let purchaseItemsWithPoints = dom.getElementById("purchaseItemsWithPoints_" + sku);
                let pointTotalPrice = dom.getElementById("pointTotalPrice_" + sku);
                let pointTotalPriceBadge = dom.getElementById("pointTotalPriceBadge_" + sku);
                let pointPriceBadge = dom.getElementById("pointPriceBadge_" + sku);

                // Update the point price badge with points from the shopping cart per sku.
                !pointPriceBadge || (pointPriceBadge.innerHTML = parseFloat(points).toFixed(2));

                // Only style elements that are present in the DOM:
                let display = (dom.getElementById("purchaseWithPointsCheckbox_" + sku)
                    && dom.getElementById("purchaseWithPointsCheckbox_" + sku).checked) ? "initial" : "none";
                !pointPrice || (pointPrice.style.display = display);
                !purchaseItemsWithPoints || (purchaseItemsWithPoints.style.display = display);

                if (pointTotalPrice) {
                    pointTotalPrice.style.display = display;
                    if (orderPointTotalBadge) {
                        // now points fields are shown
                        if (pointTotalPrice.style.display !== "none" && pointPriceBadge) {
                            if (pointPriceBadge.innerHTML) {
                                // When the checkbox is first selected, only one item will be opted in for points.
                                // So update the point total price badge to match the point price badge:
                                pointTotalPriceBadge.innerHTML = pointPriceBadge.innerHTML;
                            }
                            // When checked one item is point-redeemed:
                            _setPointTotalPriceOnShoppingCart(sku, 1, parseFloat(pointPriceBadge.innerHTML));
                        }
                        // now points fields are hidden
                        else if (pointTotalPriceBadge) {
                            // reset number increment factor:
                            dom.getElementById("" +
                                "Increment_" + sku).value = 1;
                            // When unchecked no items are point-redeemed:
                            _setPointTotalPriceOnShoppingCart(sku, 0, 0);
                        }
                        // update the points with updated DOM fields
                        _updateOrderPointTotalBadges(_getPointTotalPriceOnShoppingCart());

                    }
                }
            } else {
                throw new _skuFormatException(sku);
            }
        } catch (e) {
            console.warn(e.message);
        }
    }

    /**
     * Exception prototype.
     * @ignore
     */
    function _skuFormatException(value) {
        this.value = '';
        this.message = 'SkuFormatException: ';

        if (value) {
            this.value = value;
            this.message += 'The value provided does not conform to a SKU. Value is: "' + this.value + '"';
            this.toString = function () {
                return this.message + this.value;
            }
        } else {
            this.message += 'The value provided was undefined. Provide a valid SKU.';
            this.toString = function () {
                return this.message;
            }
        }
    }

    /**
     * Update the order point totals. An order point total can't go below zero. This function is just a
     * glorified DOM setter and only sets the points within the DOM along with some error handling.
     *
     * @param points is the total amount of points tied to a specific SKU that a user wishes to apply
     * to their order. This function accepts both number (digit) representations of points as well as strings
     * for inner HTML support. Strings are casted to numbers so be careful not to pass in two strings that
     * represent integers.
     *
     * For example calling the function with:
     *
     * _updateOrderPointTotalBadges("100" + "50");
     *
     * Which could look something like:
     *
     * _updateOrderPointTotalBadges(document.getElementById("foo").innerHTML
     *     + document.getElementById("boo").innerHTML);
     *
     * Results in a concatenated string of "10050" and will result in an integer of 10050.
     *
     * When it should really be two numbers added resulting in: 150
     *
     * In that scenario simply do the following to avoid unwanted string casting:
     *
     * var foo = document.getElementById("foo").innerHTML;
     * var boo = document.getElementById("boo").innerHTML;
     * if (foo && boo) {
     *     _updateOrderPointTotalBadges(parseInt(fooVal) + parseInt(booVal));
     * } else {
     *     console.warn("'foo' or 'boo' are not set. Look into this.");
     * }
     * @ignore
     */
    function _updateOrderPointTotalBadges(points) {
        // Cast if it's not already:
        points = (points && typeof points !== "number") ? parseFloat(points) : points;
        // Go through all of the elements with the orderPointTotalBadge class and update their points
        $(".orderPointTotalBadge").each((index, orderPointTotalBadge) => {
            orderPointTotalBadge.innerHTML = points.toFixed(2);
            // Update total points (round non-positive numbers to zero without any decimal places):
            if (orderPointTotalBadge.innerHTML <= 0) {
                orderPointTotalBadge.innerHTML = "0";
            }
        });
    }

    /**
     * When the number increment component is loaded subscribe for increment/decrement events with this function.
     * This maps the SKU to the points number increment component for which it is associated.
     *
     * Note: For the registration to work, a valid sku must be supplied and the points must be greater than zero.
     *
     * @param sku is a string representing the product sku.
     * @param productPointPrice is the points set per sku product. For example Glacial Marine Mud might have
     * a point price of 100 whereas the AP24 toothpaste might have a point price of just 25. It just depends on
     * the product. This mapping needs to be pulled in from the backend.
     * @ignore
     */
    function _registerSkuWithNumberIncrementEvent(sku, productPointPrice) {
        try {
            if (sku && typeof sku === 'string' && SKU_REGEX.test(sku) && (productPointPrice > 0)) {
                let el = dom.getElementById('nsNumberIncrement_' + sku);
                el.addEventListener('value-changed', function (data) {
                    let count = data.detail.value;
                    if (count) {
                        let pointTotalPriceBadge = document.getElementById("pointTotalPriceBadge_" + sku);
                        if (pointTotalPriceBadge) {
                            let pointsOnItem = count * productPointPrice;
                            pointTotalPriceBadge.innerHTML = parseFloat(pointsOnItem).toFixed(2);

                            _setPointTotalPriceOnShoppingCart(sku, count, pointsOnItem);
                            _updateOrderPointTotalBadges(_getPointTotalPriceOnShoppingCart());
                        }
                    }
                });
            } else {
                throw new _skuFormatException(sku);
            }
        } catch (e) {
            console.warn(e.message);
        }
    }

    function hasPointsPrice(sku) {
        if (!shoppingCart) return false;

        let filteredCart = shoppingCart.getItems({cartOrderItems: true})
            .filter(item => item.getProduct().sku === sku)
            .map(item => item.getProduct());

        if (filteredCart && filteredCart.length > 0) {
            let price = filteredCart[0].getPoints();
            return Number.isFinite(price); // Tests whether value is numeric
        } else {
            return false;
        }
    }

    function getPointsPrice(sku) {
        if (!shoppingCart) return null;

        let result = shoppingCart.getItems({cartOrderItems: true})
            .filter(item => item.getProduct().sku === sku)
            .map(item => item.getProduct().getPoints());
        return (result && result.length > 0) ? result[0] : null;
    }

    return {
        payForShippingUsingPoints,
        singleItemForPointPurchase,
        togglePointFields,
        initPoints,
        resetPoints,
        resetPayForShippingUsingPoints,
        getPointsPrice,
        hasPointsPrice
    }
}();

export default PointsCheckoutService;
